import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { CONTACT } from '../data/site';

const NAV_LINKS = [
  { id: 'about', label: 'Maison' },
  { id: 'prestations', label: 'Prestations' },
  { id: 'bar', label: 'Bar mobile' },
  { id: 'cocktails', label: 'Cocktails' },
  { id: 'reviews', label: 'Avis' },
  { id: 'faq', label: 'FAQ' },
  { id: 'contact', label: 'Contact' },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1.1, delay: 0.4, ease: [0.7, 0, 0.2, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled ? 'bg-cream/85 backdrop-blur-xl border-b border-char/10' : 'bg-transparent'
        }`}
        data-testid="site-nav"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <button
            onClick={() => scrollTo('top')}
            className="flex items-center gap-3 group"
            data-testid="nav-logo"
          >
            <span className={`font-serif italic text-2xl tracking-tight ${scrolled ? 'text-char' : 'text-white'}`}>Ô</span>
            <span className={`hidden sm:block overline group-hover:text-gold transition-colors ${scrolled ? 'text-char/60' : 'text-white/70'}`}>
              Fil des Verres
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                className={`text-[13px] tracking-wide link-underline ${scrolled ? 'text-char/70 hover:text-char' : 'text-white/70 hover:text-white'}`}
                data-testid={`nav-${l.id}`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => scrollTo('contact')}
              className="hidden md:inline-flex items-center gap-2 border border-gold/60 text-gold px-5 py-2.5 text-[12px] tracking-[0.2em] uppercase hover:bg-gold hover:text-ink transition-colors duration-500"
              data-testid="nav-devis-btn"
            >
              Demander un devis
            </button>
            <button
              onClick={() => setOpen(true)}
              className={`lg:hidden p-2 ${scrolled ? 'text-char' : 'text-white'}`}
              aria-label="Menu"
              data-testid="mobile-menu-open"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] bg-cream"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-char/10">
              <span className="font-serif italic text-2xl">Ô Fil des Verres</span>
              <button onClick={() => setOpen(false)} className="text-char p-2" data-testid="mobile-menu-close">
                <X size={22} />
              </button>
            </div>
            <div className="px-6 py-10 flex flex-col gap-6">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => scrollTo(l.id)}
                  className="text-left font-serif text-4xl text-char hover:text-gold transition-colors"
                  data-testid={`mobile-nav-${l.id}`}
                >
                  {l.label}
                </motion.button>
              ))}
              <a
                href={`tel:${CONTACT.phoneIntl}`}
                className="mt-6 overline text-gold"
                data-testid="mobile-phone"
              >
                {CONTACT.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
